/**
 * Responsive image component with:
 * - <picture> element with WebP source + JPEG fallback
 * - srcset for responsive loading
 * - Blur placeholder (LQIP) as background while loading
 * - Lazy loading + async decoding by default
 */

import { useState } from 'react';

interface ResponsiveImageProps {
  src: string;
  alt?: string;
  srcset?: string;
  webpSrcset?: string;
  blurDataUrl?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  className?: string;
  style?: React.CSSProperties;
}

export function ResponsiveImage({
  src,
  alt = '',
  srcset,
  webpSrcset,
  blurDataUrl,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 1200px',
  loading = 'lazy',
  className = '',
  style,
}: ResponsiveImageProps) {
  const [loaded, setLoaded] = useState(false);

  const blurStyle: React.CSSProperties | undefined = blurDataUrl && !loaded
    ? {
        backgroundImage: `url(${blurDataUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  const img = (
    <img
      src={src}
      srcSet={srcset}
      sizes={srcset ? sizes : undefined}
      alt={alt}
      loading={loading}
      decoding="async"
      className={`transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      style={{ ...blurStyle, ...style }}
      onLoad={() => setLoaded(true)}
    />
  );

  // If we have WebP srcset, use <picture> for format negotiation
  if (webpSrcset) {
    return (
      <picture>
        <source type="image/webp" srcSet={webpSrcset} sizes={sizes} />
        {srcset && <source type="image/jpeg" srcSet={srcset} sizes={sizes} />}
        {img}
      </picture>
    );
  }

  return img;
}
